import {Assignees} from '@github-ui/assignees'
import {ERRORS} from '@github-ui/item-picker/Errors'
import {HOTKEYS} from '@github-ui/item-picker/Hotkeys'
import type {
  AssigneePickerAssignee$data,
  AssigneePickerAssignee$key,
} from '@github-ui/item-picker/AssigneePicker.graphql'

import {commitUpdateIssueAssigneesMutation} from '@github-ui/item-picker/updateIssueAssigneesMutation'
import {
  AssigneeFragment,
  AssigneeRepositoryPicker,
  AssigneeePickerViewerGraphqlQuery,
  LazyAssigneeIssuePicker,
} from '@github-ui/item-picker/AssigneePicker'
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useMemo, Suspense, useCallback, useState, type HTMLAttributes} from 'react'
import {readInlineData, useRelayEnvironment} from 'react-relay'
import {graphql, useFragment, type PreloadedQuery, usePreloadedQuery} from 'react-relay/hooks'

import {LABELS} from '../../constants/labels'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {SectionHeader} from '../SectionHeader'
import {Section} from '../Section'
import {Link, SelectPanel, type SxProp} from '@primer/react'
import {useKeyPress} from '@github-ui/use-key-press'
import type {AssigneesSectionFragment$key} from './__generated__/AssigneesSectionFragment.graphql'
import type {AssigneesSectionAssignees$key} from './__generated__/AssigneesSectionAssignees.graphql'
import type {AssigneePickerViewerQuery} from '@github-ui/item-picker/AssigneePickerViewerQuery.graphql'
import {TEST_IDS} from '../../constants/test-ids'

const ReadonlyAssigneesSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.assignees} />
const AssigneesSectionHeader = (props: HTMLAttributes<HTMLElement>) => (
  <SectionHeader title={LABELS.sectionTitles.assignees} buttonProps={props} />
)

type AssigneesSectionProps = {
  sectionHeader: JSX.Element
  onSelfAssignClick: () => void
  assignees: AssigneePickerAssignee$data[]
  readonly: boolean
} & SxProp
const AssigneesSection = ({sectionHeader, onSelfAssignClick, assignees, readonly}: AssigneesSectionProps) => {
  const emptyHeader = !readonly ? (
    <>
      {LABELS.emptySections.assignees(true)}
      <Link
        tabIndex={0}
        onClick={onSelfAssignClick}
        sx={{color: 'fg.accent', fontWeight: 400, cursor: 'pointer', '&:hover': {textDecoration: 'none'}}}
      >
        {LABELS.emptySections.selfAssign}
      </Link>
    </>
  ) : (
    LABELS.emptySections.assignees(false)
  )

  return (
    <Section sectionHeader={sectionHeader} emptyText={assignees.length > 0 ? undefined : emptyHeader}>
      <Assignees assignees={assignees} testId={TEST_IDS.assignees} />
    </Section>
  )
}

export type CreateIssueAssigneesSectionProps = {
  repoName: string
  repoOwner: string
  readonly: boolean
  assignees: AssigneePickerAssignee$data[]
  onSelectionChange: (value: AssigneePickerAssignee$data[]) => void
  viewerRef: PreloadedQuery<AssigneePickerViewerQuery, Record<string, unknown>>
  insidePortal: boolean
  shortcutEnabled: boolean
  shortcutHintVisible: boolean
} & SxProp
export function CreateIssueAssigneesSection({
  repoName,
  repoOwner,
  readonly,
  assignees,
  onSelectionChange,
  viewerRef,
  sx,
  ...sharedConfigProps
}: CreateIssueAssigneesSectionProps) {
  const data = usePreloadedQuery(AssigneeePickerViewerGraphqlQuery, viewerRef)
  // eslint-disable-next-line no-restricted-syntax
  const viewer = readInlineData<AssigneePickerAssignee$key>(AssigneeFragment, data.viewer)

  const sectionHeader = readonly ? (
    <ReadonlyAssigneesSectionHeader />
  ) : (
    <AssigneeRepositoryPicker
      repoName={repoName}
      repoOwner={repoOwner}
      readonly={readonly}
      assignees={assignees}
      participants={[]}
      onSelectionChange={onSelectionChange}
      anchorElement={AssigneesSectionHeader}
      {...sharedConfigProps}
    />
  )

  return (
    <AssigneesSection
      sectionHeader={sectionHeader}
      onSelfAssignClick={() => onSelectionChange([viewer])}
      assignees={assignees}
      readonly={readonly}
      sx={sx}
    />
  )
}

const assigneesSectionAssignees = graphql`
  fragment AssigneesSectionAssignees on Issue {
    assignees(first: 20) {
      edges {
        node {
          ...AssigneePickerAssignee
        }
      }
    }
  }
`

const assigneesSectionFragment = graphql`
  fragment AssigneesSectionFragment on Issue {
    id
    number
    repository {
      name
      owner {
        login
      }
      isArchived
    }
    ...AssigneesSectionAssignees
    participants(first: 10) {
      edges {
        node {
          ...AssigneePickerAssignee
        }
      }
    }
    # eslint-disable-next-line relay/unused-fields
    viewerCanUpdateNext
    viewerCanAssign
  }
`

type Assignee = AssigneePickerAssignee$data

export type EditIssueAssigneesSectionProps = {
  issue: AssigneesSectionFragment$key
  viewer: AssigneePickerAssignee$key
  onIssueUpdate?: () => void
  singleKeyShortcutsEnabled: boolean
  prefetchInteractionDataEnabled: boolean
}
export function EditIssueAssigneesSection({
  issue,
  viewer,
  onIssueUpdate,
  singleKeyShortcutsEnabled,
  prefetchInteractionDataEnabled,
}: EditIssueAssigneesSectionProps) {
  const data = useFragment(assigneesSectionFragment, issue)
  const {
    repository: {
      owner: {login: owner},
      name: repo,
      isArchived: isRepositoryArchived,
    },
    number,
  } = data
  const assigneesData = useFragment<AssigneesSectionAssignees$key>(assigneesSectionAssignees, data)
  // eslint-disable-next-line no-restricted-syntax
  const viewerData = readInlineData(AssigneeFragment, viewer)

  const {id: issueId, viewerCanAssign} = data

  const participants = (data.participants?.edges || []).flatMap(participant =>
    participant && participant.node
      ? // eslint-disable-next-line no-restricted-syntax
        [readInlineData<AssigneePickerAssignee$key>(AssigneeFragment, participant.node)]
      : [],
  )

  const assignees = useMemo(() => {
    return (assigneesData.assignees?.edges || []).flatMap(assignee =>
      // eslint-disable-next-line no-restricted-syntax
      assignee && assignee.node ? [readInlineData<AssigneePickerAssignee$key>(AssigneeFragment, assignee.node)] : [],
    )
  }, [assigneesData])

  const {addToast} = useToastContext()
  const environment = useRelayEnvironment()

  const updateAssignees = useCallback(
    (selectedAssignees: Assignee[]) => {
      commitUpdateIssueAssigneesMutation({
        environment,
        input: {issueId, assignees: selectedAssignees, participants},
        onError: () =>
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateAssignees,
          }),
        onCompleted: onIssueUpdate,
      })
    },
    [addToast, environment, issueId, onIssueUpdate, participants],
  )

  const readonlyAssignee = !viewerCanAssign || isRepositoryArchived

  const assigneeSectionHeader = useMemo(() => {
    if (readonlyAssignee) {
      return <ReadonlyAssigneesSectionHeader />
    }

    return prefetchInteractionDataEnabled ? (
      <Suspense fallback={<ReadonlyAssigneesSectionHeader />}>
        <LazyAssigneeIssuePicker
          itemIdentifier={{owner, repo, number}}
          readonly={readonlyAssignee}
          shortcutEnabled={singleKeyShortcutsEnabled}
          assignees={assignees}
          viewer={viewerData}
          anchorElement={AssigneesSectionHeader}
          onSelectionChange={(selectedAssignees: Assignee[]) => updateAssignees(selectedAssignees)}
        />
      </Suspense>
    ) : (
      <LazyAssigneeIssuePickerOnClick
        itemIdentifier={{owner, repo, number}}
        readonly={readonlyAssignee}
        shortcutEnabled={singleKeyShortcutsEnabled}
        assignees={assignees}
        viewer={viewerData}
        onSelectionChange={(selectedAssignees: Assignee[]) => updateAssignees(selectedAssignees)}
      />
    )
  }, [
    readonlyAssignee,
    prefetchInteractionDataEnabled,
    owner,
    repo,
    number,
    singleKeyShortcutsEnabled,
    assignees,
    viewerData,
    updateAssignees,
  ])

  return (
    <AssigneesSection
      sectionHeader={assigneeSectionHeader}
      assignees={assignees}
      onSelfAssignClick={() => updateAssignees([viewerData])}
      readonly={readonlyAssignee}
    />
  )
}

type LazyAssigneeIssuePickerOnClickProps = {
  itemIdentifier: {owner: string; repo: string; number: number}
  readonly: boolean
  shortcutEnabled: boolean
  assignees: AssigneePickerAssignee$data[]
  viewer: AssigneePickerAssignee$data
  onSelectionChange: (selectedAssignees: Assignee[]) => void
}

function LazyAssigneeIssuePickerOnClick({
  itemIdentifier,
  readonly,
  shortcutEnabled,
  assignees,
  viewer,
  onSelectionChange,
}: LazyAssigneeIssuePickerOnClickProps) {
  const [wasTriggered, setWasTriggered] = useState(false)
  const [searchText, setSearchText] = useState('')

  useKeyPress(shortcutEnabled ? [HOTKEYS.assigneePicker] : [], (e: KeyboardEvent) => {
    if (!wasTriggered) {
      setWasTriggered(true)
      e.preventDefault()
    }
  })

  if (!wasTriggered) {
    return <AssigneesSectionHeader onClick={() => setWasTriggered(true)} />
  }

  return (
    <Suspense
      fallback={
        <SelectPanel
          loading={true}
          placeholderText={'Filter assignees'}
          open
          onOpenChange={() => {}}
          onSelectedChange={() => {}}
          items={[]}
          filterValue={searchText}
          onFilterChange={setSearchText}
          selected={[]}
          renderAnchor={AssigneesSectionHeader}
          overlayProps={{width: 'medium', height: 'auto'}}
        />
      }
    >
      <LazyAssigneeIssuePicker
        itemIdentifier={itemIdentifier}
        readonly={readonly}
        shortcutEnabled={shortcutEnabled}
        assignees={assignees}
        triggerOpen={true}
        viewer={viewer}
        anchorElement={AssigneesSectionHeader}
        onSelectionChange={onSelectionChange}
      />
    </Suspense>
  )
}

try{ ReadonlyAssigneesSectionHeader.displayName ||= 'ReadonlyAssigneesSectionHeader' } catch {}
try{ AssigneesSectionHeader.displayName ||= 'AssigneesSectionHeader' } catch {}
try{ AssigneesSection.displayName ||= 'AssigneesSection' } catch {}
try{ CreateIssueAssigneesSection.displayName ||= 'CreateIssueAssigneesSection' } catch {}
try{ EditIssueAssigneesSection.displayName ||= 'EditIssueAssigneesSection' } catch {}
try{ LazyAssigneeIssuePickerOnClick.displayName ||= 'LazyAssigneeIssuePickerOnClick' } catch {}